import type {
    FieldPath,
    FieldPathValue,
    UseFormSetValue,
} from 'react-hook-form';

/**
 * This function touches the provided field.
 */
const touchAllFields = <T extends Record<string, unknown>>(
    // The values to use for looping and nesting
    values: unknown,
    // The setValue function from the form context
    setValue: UseFormSetValue<T>,
    // The setValue function from the form context
    config?: {
        // Whether or not to dirty the touched fields
        shouldDirty?: boolean;
        // Optional path argument, although only used when function calls itself
        path?: Array<string>;
    }
): void => {
    const shouldDirty: boolean | undefined = config?.shouldDirty;
    const path: Array<string> = config?.path ?? [];

    if (typeof values !== 'object' || values === null) return;

    const entries: Array<[string, unknown]> = Array.isArray(values)
        ? (values as Array<unknown>).map((item, idx): [string, unknown] => [
              String(idx),
              item,
          ])
        : Object.entries(values as Record<string, unknown>);

    entries.forEach(([key, value]) => {
        const currentPath = [...path, key];
        const newPath = currentPath.join('.');
        /**
         * If the value is an object or array, and it's not null, we rerun this function for that value.
         * Unless we are at materialColour, in that case we just touch that field.
         */
        if (
            typeof value === 'object' &&
            value !== null &&
            key !== 'materialColour'
        ) {
            touchAllFields(value, setValue, { shouldDirty, path: currentPath });
        } else {
            setValue(
                newPath as unknown as FieldPath<T>,
                value as FieldPathValue<T, FieldPath<T>>,
                {
                    shouldTouch: true,
                    shouldDirty,
                    /**
                     * This function does not validate, because validation can be heavy, especially on batch updates.
                     * If validation is needed, call the trigger() function from the form context instead
                     */
                    shouldValidate: false,
                }
            );
        }
    });
};

export { touchAllFields };
